import React, { useRef } from "react";
import PatientInfo from "./PatientInfo";
import TestResultsTable from "./TestResultsTable";
import HealthLogo from "../../assets/img/healthlogo.svg";
import { Link } from "react-router-dom";
import { BaseButton } from "../base";
import jsPDF from "jspdf";
import { BsCloudDownloadFill } from "react-icons/bs";
import { Order, OrderDetails } from "../../store/slices/order/types";
import { getAvailablePanelResults } from "../../utils/result.utils";
import moment from "moment";
import biotracka from "../../assets/img/result/biotracka logo.png";
import PdfResult from "./PdfResult";

type CustomerTestResultsProp = {
  orderDetails: Order | null;
};
const CustomerTestResults: React.FC<CustomerTestResultsProp> = ({
  orderDetails,
}) => {
  const resultRef = useRef<HTMLDivElement | null>(null);
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const result = orderDetails?.result;
  const panelResults = result?.panelResults || [];

  const availablePanelResults = getAvailablePanelResults(panelResults);
  function calculateAge(birthdate: string) {
    const now = new Date();
    const birthDate = new Date(birthdate);

    let years = now.getFullYear() - birthDate.getFullYear();
    let months = now.getMonth() - birthDate.getMonth();
    let days = now.getDate() - birthDate.getDate();

    // Adjust months and years if necessary
    if (days < 0) {
      months--;
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 0);
      days += lastMonthDate.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    return { years, months, days };
  }

  // Usage
  const age = calculateAge(orderDetails?.user?.dateOfBirth!!);

  const downloadResult = () => {
    const docs = pdfRef.current;
    const pdf = new jsPDF("p", "pt", "a4");

    // Adding the fonts
    pdf.setFont("Open Sans", "normal");
    pdf.setFontSize(12);
    if (docs) {
      docs.style.display = "block";
      pdf.html(docs, {
        callback: (doc) => {
          const pageCount = doc.getNumberOfPages();
          const x =
            doc.getCurrentPageInfo().pageContext.mediaBox.topRightX - 60;
          const y =
            doc.getCurrentPageInfo().pageContext.mediaBox.topRightY - 15;
          const pageWidth =
            doc.getCurrentPageInfo().pageContext.mediaBox.topRightX;
          const text = "Saving lives through innovative pathologist services";
          const textWidth = doc.getTextWidth(text);

          const xa = (pageWidth - textWidth) / 2;
          const ya =
            doc.getCurrentPageInfo().pageContext.mediaBox.topRightY - 15; // Adjust as necessary

          doc.setFontSize(8);
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.text(text, xa, ya);
            doc.text(`Page ${i} of ${pageCount}`, x, y);
          }
          // if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
          //   {
          //        window.open(doc.output("bloburl"), "_blank");
          //   }
          //   else
          //   {
          //     doc.save(`${orderDetails?.uniqueId}-lab-test-results.pdf`);
          //   }

          window.open(doc.output("bloburl"), "_blank");
        },
        margin: [30, 5, 50, 5],
        x: 12,
        y: 12,
        width: 550,
        windowWidth: 800,
        autoPaging: true,
      });
      setTimeout(() => {
        docs.style.display = "none";
      }, 1000);
    }
  };

  return (
    <div className="bg-[#F4F6F8] mt-6 p-4 lg:mx-12 lg:px-10">
      <div className="flex justify-end w-full mt-4">
        <BaseButton
          text="Download"
          type="button"
          variant="primary"
          styles="px-4 !py-2 !rounded-[1rem] !font-semibold"
          click={downloadResult}
          hasRightIcon
        >
          <BsCloudDownloadFill className="mx-1" />
        </BaseButton>
      </div>

      <div ref={resultRef} className="lg:py-8">
        <div className="flex flex-row justify-between items-center mb-6 lg:mt-[4px] md:my-6 cursor-pointer lg:px-2">
          <div>
            <h3 className="text-[#0C4654] text-[1rem] font-[500]">
              Lab Result
            </h3>
          </div>
          <Link to="/">
            <img
              src={HealthLogo}
              alt="logo"
              className="w-[100px] sm:w-[150px] lg:w-[120px]"
            />
          </Link>
        </div>

        <PatientInfo orderDetails={orderDetails} />
        {availablePanelResults.map((panelResult) => (
          <div key={panelResult._id}>
            <TestResultsTable panelResult={panelResult} />
          </div>
        ))}
        <div>
          <p className="text-[0.875rem] leading-6  text-center text-[#525256] font-[400] mt-[-60px] mb-7">
            This result is a property of healthtracka and we’re not responsible
            for any alteration that may occur
          </p>
        </div>
      </div>
      <main ref={pdfRef} className="px-3 hidden">
        <div className="flex items-center justify-between text-[12px]">
          <img src={biotracka} alt="biotracka logo" width={180} height={60} />
          <div className="text-center">
            <p>
              www.healthtracka.com |{" "}
              <span className="tracking-wide">results@healthtracka.com</span>{" "}
            </p>
            <p className="font-bold">09029166150 | 08144891658</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 items-center justify-between mt-2">
          <p className="border-2 border-black p-2 font-bold flex items-center pb-6">
            <span className="pr-2">Requisition</span> Number:{" "}
            {orderDetails?.result?.barcode}
          </p>
          <p className="border-2 border-black p-2 font-bold flex items-center pb-6 w-full">
            <span className="pr-2">Request</span> Date:{" "}
            {`${moment(orderDetails?.result?.requisitionDate).format(
              " DD/MM/YYYY"
            )} ${moment(orderDetails?.result?.requisitionDate)
              .format("HH:MM")
              .replace(":", " : ")}`}{" "}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-8 mt-10 text-[12px]">
          <div>
            <div className="grid grid-cols-2 gap-3">
              <p className="font-bold">Patient name</p>
              <p>
                :{" "}
                <span className="capitalize">
                  {orderDetails?.user?.firstName}
                </span>{" "}
                <span className="capitalize">
                  {orderDetails?.user?.lastName}
                </span>{" "}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <p className="font-bold">Age / Sex</p>
              <p>
                : {age.years} years, {age.months} months, {age.days} days /
                {orderDetails?.user?.gender && (
                  <span>{orderDetails?.user?.gender[0]}</span>
                )}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <p className="font-bold">Collection Date & Time</p>
              <p>
                :{" "}
                {`${moment(orderDetails?.result?.collectionDate).format(
                  "DD/MM/YYYY"
                )} ${moment(orderDetails?.result?.collectionDate)
                  .format("HH:MM")
                  .replace(":", "  :")}`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-5 mt-3">
              <p className="font-bold ">Reffered by</p>
              <p className="tracking-wide">
                : {orderDetails?.result?.referredBy}
              </p>
            </div>
          </div>
          <div>
            <div className="grid grid-cols-2 gap-5">
              <p className="font-bold">Barcode number</p>
              <p>: {orderDetails?.result?.barcode}</p>
            </div>
            <div className="grid grid-cols-2 gap-5 mt-3">
              <p className="font-bold">
                {" "}
                <span className="pr-2">Accession</span> Date & Time
              </p>
              <p className="flex items-center gap-2">
                <span>
                  :{" "}
                  {moment(orderDetails?.result?.accessionDate).format(
                    "DD/MM/YYYY"
                  )}
                </span>
                <span>
                  {moment(orderDetails?.result?.accessionDate)
                    .format("HH:MM")
                    .replace(":", "  :")}
                </span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-5 mt-3">
              <p className="font-bold">Report Date & Time</p>
              <p>
                :{" "}
                {`${moment(orderDetails?.result?.reportDate).format(
                  "DD/MM/YYYY"
                )} ${moment(orderDetails?.result?.reportDate)
                  .format("HH:MM")
                  .replace(":", "  :")}`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-5 mt-3">
              <p className="font-bold">Clinical history</p>
              <p>: </p>
            </div>
            <div className="grid grid-cols-2 gap-5 mt-3">
              <p className="font-bold">Printed Date & Time </p>
              <p>
                :{" "}
                {`${moment(orderDetails?.result?.printedDate).format(
                  "DD/MM/YYYY"
                )} ${moment(orderDetails?.result?.printedDate)
                  .format("HH:MM")
                  .replace(":", "  :")}`}
              </p>
            </div>
          </div>
        </div>
        <p className="mt-5 italic text-[12px]">
          Thank you for your request. We are reporting the following results:
        </p>
        <div className="mt-10">
          {availablePanelResults &&
            availablePanelResults.map((panelResult) => (
              <PdfResult key={panelResult._id} panelResult={panelResult} />
            ))}
        </div>
        <p className="my-8 text-center underline text-[14px]">End of Report</p>

        <div className="mt-9">
          <p className="text-right text-[13px]">Approved by</p>
          <p className="text-right text-[14px]">ABIODUN SEYI</p>
        </div>
      </main>
    </div>
  );
};

export default CustomerTestResults;
